import {action, computed, observable, reaction, runInAction} from 'mobx';
import {getId, getName, isStudent} from "../../utils/userUtility";
import manageStore from "../../stores/ManageStore";
import realtimeStore from "../../stores/RealtimeStore";
import dialogStore from "../../containers/dialog/store";
import {DialogTypes, NotificationType, RemoteVideoState, VideoNotificationType} from "../../constants/enums";
import moment from "moment";
import request from "../../utils/request";
import {NOTIFICATION} from "../../constants/urlConstants";
import {isDesktop} from "../../utils/screenUtility";
import userStore from "../../stores/UserStore";
import notificationStore from "../../stores/NotificationStore";
import debounce from "lodash/debounce";

import {videoWebrtcManager} from "./VideoWebrtcManager";
import {AskToJoinStudentDialogId, AskToJoinTeacherDialogId, DeclineStudentDialogId, StudentDeclinedDialogId} from "./VideoChat";


const NETWORK_STATS_INTERVAL = 5000;
const ROOM_INTERVAL = 1000 * 60 * 60; // 10 minutes


export class DailyVideoChatStore {
    @observable videoChatStarted = false;

    @observable streamError = '';
    @observable selectedVideoDeviceId = 'default';
    @observable selectedAudioDeviceId = 'default';

    @observable audioDevices = [];
    @observable videoDevices = [];

    @observable audioMuted = false;
    @observable videoMuted = false;
    @observable screenShared = false;
    @observable speakerMuted = false;

    @observable remoteAudioMuted = false;
    @observable remoteVideoMuted = false;

    @observable peerUser = null;
    @observable lastDeclinedUser = null;

    @observable remoteVideoState = RemoteVideoState.Init;
    @observable minimizeMode = false;

    @observable videoStartCounter = 0;

    @observable videoFacingMode = 'user';
    @observable fullScreen = false;
    @observable screenShareStreamId = null;
    @observable peerIsInRoom = false;

    @observable localMirroringActive = false;
    @observable remoteMirroringActive = false;

    @observable networkStats = {threshold: 'good'};

    dialogActions = {
        closeDialog: () => null,
        openDialog: () => null,
        isDialogOpen: () => null
    };

    numTimesTried = 0;

    videoStreamSettings = {};

    videoStartCounterInterval = null;

    devicesFetched = false;
    currentDeviceIndex = 0;

    stream = null;
    remoteStream = {};

    localVideoRef = null;
    remoteVideoRef = null;
    remoteAudioRef = null;
    remoteScreenAudioRef = null;
    fullScreenVideoRef = null;
    previewVideoRef = null;
    iceConnected = false;
    remoteClients = {};

    counter = 0;
    tracksAdded = false;

    videoRef = null;

    notificationCount = 0;


    getDataChannel = (id) => {
        return `general:${id}:video`;
    }

    @computed get showNetworkWarning() {
        switch (this.networkStats?.threshold) {
            case 'low':
            case 'very-low':
                return true;
            default:
                return false;
        }
    }


    constructor() {
        setTimeout(() => {
            reaction(() => userStore.isAuthenticated, (isAuthenticated) => {
                if (isAuthenticated) {
                    realtimeStore.subscribe(this.getDataChannel(getId()), this.onRemoteDataMessage);
                }
            });

            reaction(() => notificationStore.userNotifications, async (userNotifications) => {
                const enterRoomNotifications = userNotifications.filter(n => n.objectType === NotificationType.Video && n.type === VideoNotificationType.EnterRoom);
                if (enterRoomNotifications.length === 0) {
                    return;
                }
                if (this.notificationCount === enterRoomNotifications.length && this.videoChatStarted) {
                    console.log('ignoring video chat request, video already started with the same user');
                    return;
                }
                const enterRoom = enterRoomNotifications[0];
                if (enterRoom && (Date.now() - new Date(enterRoom.created).getTime() <= ROOM_INTERVAL)) {
                    console.log('found enter room notification within interval', this.getPeer, enterRoom.from?.id);
                    if (!this.peerUser || this.getPeer === enterRoom.from?.id) {
                        console.log('sending are you in room');
                        this.setPeerUser(enterRoom.from);
                        this.sendToOtherSide({type: 'are-you-in-room'});
                    } else if (this.getPeer !== enterRoom.from?.id && !isStudent()) {
                        this.decline(enterRoom.from?.id, true);
                    }
                    // else if (!this.getPeer && isStudent() && !this.videoChatStarted) {
                    //     await this.activateVideoChat();
                    // }
                }
                this.notificationCount = enterRoomNotifications.length;
            });
        });
    }

    setDialogActions = (dialogActions) => {
        this.dialogActions = dialogActions;
    }

    @action setRemoteVideoRef = (remoteVideoRef) => {
        this.remoteVideoRef = remoteVideoRef;
        this.updateRemoteRef();
    }

    @action setRemoteAudioRef = (remoteAudioRef) => {
        this.remoteAudioRef = remoteAudioRef;
        this.updateRemoteRef();
    }

    @action setRemoteScreenAudioRef = (remoteScreenAudioRef) => {
        this.remoteScreenAudioRef = remoteScreenAudioRef;
        this.updateRemoteRef();
    }

    @action setRemoteVideoScreenShareRef = (remoteVideoScreenShareRef) => {
        this.remoteVideoScreenShareRef = remoteVideoScreenShareRef;
        this.updateRemoteRef();
    }

    @action setFullScreenVideoRef = (fullScreenVideoRef) => {
        this.fullScreenVideoRef = fullScreenVideoRef;
        this.updateRemoteRef();
    }

    updateRemoteParticipant = (participant) => {
        const videoStream = this.getVideoStream(participant);
        const audioStream = this.getAudioStream(participant);
        this.remoteVideoMuted = participant.tracks.video.state !== "playable";
        this.remoteAudioMuted = participant.tracks.audio.state !== "playable";
        this.updateRef(this.remoteVideoRef, videoStream);
        this.updateRef(this.fullScreenVideoRef, videoStream);
        this.updateRef(this.remoteAudioRef, audioStream);
        if (this.hasScreenShare) {
            const screenMediaStream = this.getScreenStream(participant)
            const audioMediaStream = this.getScreenStreamAudio(participant);
            console.log('got screen:' + screenMediaStream)
            console.log('got audio:' + audioMediaStream)
            this.updateRef(this.fullScreenVideoRef, screenMediaStream);
            this.updateRef(this.remoteScreenAudioRef, audioMediaStream);
        }
    }

    updateRemoteRef = async () => {
        if (this.viewer) {
            const participants = await this.viewer.participants() || {};
            runInAction(() => {
                Object.values(participants).filter(p => !p.local).map(p => this.updateRemoteParticipant(p));
            })
        }

    }

    @action updateRef = (htmlRef, stream) => {
        if (htmlRef && stream) {
            htmlRef.srcObject = stream;
            htmlRef.onpause = async (e) => {
                try {
                    await e.target.play();
                } catch (err) {
                    console.log(err);
                }
            };

            setTimeout(async () => {
                try {
                    await htmlRef.play();
                } catch (err) {
                    console.log(err);
                }
            });
        }
    }

    @action updateAudioRef = (audioRef, stream) => {
        if (audioRef && stream) {
            audioRef.srcObject = stream;
            audioRef.onpause = async (e) => {
                try {
                    await e.target.play();
                } catch (err) {
                    console.log(err);
                }
            };

            setTimeout(async () => {
                try {
                    await audioRef.play();
                } catch (err) {
                    console.log(err);
                }
            });
        }
    }


    @action setLocalVideoRef = (localVideoRef) => {
        this.localVideoRef = localVideoRef;
    }

    @action startVideoChat = (peerUser) => {
        if (this.remoteVideoState === RemoteVideoState.Approved || this.remoteVideoState === RemoteVideoState.Loading || this.remoteVideoState === RemoteVideoState.VideoStreamStarted) {
            console.log('not starting a new video chat, state is: ', this.remoteVideoState);
            return;
        }
        this.setPeerUser(peerUser);
        dialogStore.openDialog(DialogTypes.StartVideoChat);
    }

    @action closeVideoChat = async (skipSending = false) => {
        if (this.getPeer) {
            await this.sendToOtherSide({type: 'end'});
        }
        await this.resetVideoState();
    }

    @action setPeerUser = (peerUser) => {
        this.peerUser = peerUser;
    }

    @computed get getPeer() {
        return this.peerUser?.id;
    }

    sendRoomNotification = async () => {
        const notification = {
            from: getId(),
            receiverId: this.getPeer,
            objectType: NotificationType.Video,
            type: VideoNotificationType.EnterRoom,
            objectId: this.getPeer,
            hide: true,
            extraInformation: {projectId: this.peerUser?.projectId}
        };
        await request(`${NOTIFICATION}`, {method: 'PUT', body: notification}, true);
    }

    sendToOtherSide = async (data) => {
        this.sendTo(this.getPeer, data);
    }

    sendTo = async (studentId, data) => {
        if (!studentId) {
            console.log('not sending data, no peer', data);
        }
        console.log('sending to:', studentId, data)
        await realtimeStore.send(this.getDataChannel(studentId), {id: getId(), ...data});
    }

    onClose = () => {
        if (this.remoteVideoState === RemoteVideoState.VideoStreamStarted) {
            this.activateVideoChat(true);
        }
    }

    startViewer = async () => {
        const room = isStudent() ? getId() : this.getPeer;
        this.viewer = await videoWebrtcManager.start(room, this.participantEventHandler, this.errorEventHandler);
        console.log(this.audioMuted);
        this.viewer.setLocalAudio(!this.audioMuted)
        this.viewer.setLocalVideo(!this.videoMuted)

        await this.updateDeviceList(true);
        this.setupDeviceChange();

        const participants = this.viewer.participants();

        Object.values(participants).map((participant) => {
            this.participantHandler(participant);
        });

        clearInterval(this.networkStatusInterval);
        this.networkStatusInterval = setInterval(() => {
            runInAction(() => {
                this.networkStats = this.viewer?.getNetworkStats();
            });
        }, NETWORK_STATS_INTERVAL)


        console.log('done creating viewer');
    }


    @action activateVideoChat = debounce(async () => {
        this.videoChatStarted = true;
        if (!isStudent() && this.peerIsInRoom) {
            this.remoteVideoState = RemoteVideoState.Approved;
        }
        await videoWebrtcManager.joinRoom();
        if (!this.peerIsInRoom) {
            await this.sendRoomNotification();
        }
    }, 500, {leading: true, trailing: false});


    getStream(track) {
        try {
            if (track) {
                return new MediaStream([track])
            }
            console.log('could not find any stream');
        } catch (err) {
            console.log('error creating mediastream:', err);
        }
        return null;
    }

    getAudioStream(participant) {
        return this.getStream(participant?.tracks?.audio?.persistentTrack)
    }

    getVideoStream(participant) {
        return this.getStream(participant?.tracks?.video?.persistentTrack)
    }

    getScreenStream(participant) {
        return this.getStream(participant?.tracks?.screenVideo?.persistentTrack)
    }

    getScreenStreamAudio(participant) {
        return this.getStream(participant?.tracks?.screenAudio?.persistentTrack)
    }

    @action errorEventHandler = (event) => {
        console.log('got error event from daily:', event);
        switch (event.action) {
            case 'camera-error':
                this.streamError = event.errorMsg?.errorMsg || 'error';
                break;
            default:
        }
    }

    @action setCounter = () => {
        if (!this.videoStartCounterInterval) {
            this.videoStartCounter = 0;
            this.videoStartCounterInterval = setInterval(() => {
                runInAction(() => {
                    this.videoStartCounter++;
                });
            }, 1000);
        }
    }

    sendMirroringMessage = () => {
        this.sendToOtherSide({type: 'mirroring', 'value': this.localMirroringActive});
    }

    @action participantEventHandler = (event) => {
        console.log('got event from daily:', event);
        if (!this.viewer) {
            console.log('no call, ignoring event');
            return;
        }
        switch (event.action) {
            case 'participant-joined':
                if (!event.participant.local) {
                    this.remoteVideoState = RemoteVideoState.VideoStreamStarted;
                    this.setCounter();
                    if (this.localMirroringActive) {
                        this.sendMirroringMessage();
                    }
                }
                break;
            case 'participant-updated':
                this.participantHandler(event.participant);
                break;
            case 'participant-left':
                if (this.remoteVideoState === RemoteVideoState.VideoStreamStarted) {
                    this.remoteVideoState = RemoteVideoState.Loading;
                }
                break;
            default:
        }
    }

    @action participantHandler = (participant) => {
        if (participant.local) {
            const videoStream = this.getVideoStream(participant);

            this.sessionId = participant.session_id;
            this.updateRef(this.previewVideoRef, videoStream);
            this.updateRef(this.localVideoRef, videoStream);
            this.audioDevices.forEach(device => {
                device.selected = device.label === participant?.tracks?.audio?.track?.label
            })
            this.videoDevices.forEach(device => {
                device.selected = device.label === participant?.tracks?.video?.track?.label
            })
            this.stream = this.getAudioStream(participant);
        } else {
            if (participant.screen && participant.tracks.screenVideo.track) {
                this.fullScreen = true;
                this.screenShareStreamId = participant.tracks.screenVideo.track.id;
            } else {
                this.screenShareStreamId = null;
            }

            this.updateRemoteParticipant(participant)

            this.remoteVideoState = RemoteVideoState.VideoStreamStarted;
        }
    }

    @computed
    get videoChatTime() {
        return moment().hour(0).minute(0).second(this.videoStartCounter).format('mm:ss');
    }

    @computed
    get noFlipLocalVideo() {
        return this.videoFacingMode === 'environment';
    }

    @computed
    get screenShareSupport() {
        return isDesktop() && navigator?.mediaDevices?.getDisplayMedia;
    }


    sendMissedCalledNotification = async (peerId) => {
        const notification = {
            receiverId: peerId,
            objectId: getId(),
            objectName: getName(),
            objectType: NotificationType.Video,
            type: VideoNotificationType.MissedCall
        };
        await request(`${NOTIFICATION}`, {method: 'PUT', body: notification});
    }

    setPeerUserFromId = (id) => {
        if (isStudent()) {
            if (!manageStore.getTeacher(id)) {
                console.log('ignoring request, manage store not initialized yet, or use not found');
                return false;
            }
            this.setPeerUser(manageStore.getTeacher(id));
        } else {
            if (!manageStore.getStudent(id)) {
                console.log('ignoring request, manage store not initialized yet, or use not found');
                return false;
            }
            this.setPeerUser(manageStore.getStudent(id));
        }
        return true;
    }

    onRemoteDataMessage = (channel, data) => {
        try {
            runInAction(async () => {
                console.log('got message', data);
                switch (data.type) {
                    case 'declined':
                        if (this.getPeer !== data.id) {
                            console.log('ignoring end request: peerUser:', this.peerUser, ', id:', data.id);
                            return;
                        }
                        dialogStore.closeDialog(DialogTypes.StartVideoChat);
                        this.resetVideoState();
                        this.lastDeclinedUser = isStudent() ? manageStore.getTeacher(data.id) : manageStore.getStudent(data.id);
                        this.dialogActions.openDialog(null, StudentDeclinedDialogId);
                        break;
                    case 'end':
                        if (this.getPeer !== data.id) {
                            console.log('ignoring end request: peerUser:', this.peerUser, ', id:', data.id);
                            return;
                        }

                        this.dialogActions.closeDialog(AskToJoinTeacherDialogId)
                        this.dialogActions.closeDialog(AskToJoinStudentDialogId)
                        this.remoteVideoState = RemoteVideoState.PeerEnd;
                        dialogStore.closeDialog(DialogTypes.StartVideoChat);
                        this.resetCounter();
                        this.videoChatStarted = false;
                        this.screenShareStreamId = null;
                        this.screenShared = false;
                        this.peerUser = null;
                        this.lastDeclinedUser = null;
                        await this.stopStream();
                        break;
                    case 'are-you-in-room':
                        if (this.videoChatStarted && data.id === this.getPeer && this.remoteVideoState !== RemoteVideoState.Declined) {
                            this.sendToOtherSide({type: 'i-am-in-room'});
                        } else {
                            this.sendToOtherSide({type: 'i-am-not-in-room'});
                        }
                        break;
                    case 'i-am-in-room':
                        this.peerIsInRoom = true;
                        const peerUser = isStudent() ? manageStore.getTeacher(data.id) : manageStore.getStudent(data.id);
                        const dialogId = isStudent() ? AskToJoinStudentDialogId : AskToJoinTeacherDialogId
                        this.setPeerUser(peerUser);
                        if (this.remoteVideoState !== RemoteVideoState.VideoStreamStarted) {
                            this.remoteVideoState = RemoteVideoState.Init;
                            this.dialogActions.openDialog(null, dialogId);
                        }
                        break;
                    case 'i-am-not-in-room':
                        this.restartingVideo = false;
                        this.peerIsInRoom = false;
                        break;
                    case 'mirroring':
                        this.remoteMirroringActive = data.value;
                        break;
                    default:
                }
            });
        } catch (err) {
            console.log(err);
        }
    }

    resetCounter() {
        clearInterval(this.videoStartCounterInterval);
        this.videoStartCounterInterval = null;
        this.videoStartCounter = 0;
    }

    @action
    resetVideoState = async () => {
        await this.stopStream();
        clearInterval(this.networkStatusInterval);
        runInAction(() => {
            this.remoteVideoState = RemoteVideoState.Init;
            this.videoChatStarted = false;
            this.peerIsInRoom = false;
            this.peerUser = null;
            this.screenShareStreamId = null;
            this.screenShared = false;
            this.lastDeclinedUser = null;
            this.counter = 0;
            this.tracksAdded = false;
            this.resatingVideo = false;
            this.resetCounter();
        })
    }

    @action
    stopStream = async () => {
        await videoWebrtcManager.stop();
        this.viewer = null;

        this.resetStream();
    }

    @action
    resetStream = () => {
        this.stream?.getTracks().forEach(track => track.stop());
        this.displayMedia?.getTracks().forEach(track => track.stop());
        this.stream = null;
        this.displayMedia = null;
    }


    @action
    approveStudent = async () => {
        this.sendToOtherSide({type: 'approved'});
        runInAction(() => {
            this.remoteVideoState = RemoteVideoState.Approved;
        })

    }

    @action
    approveStudentPending = () => {
        this.remoteVideoState = RemoteVideoState.ApprovedPending;
    }

    @action
    decline = (peerId = this.getPeer, openDeclineDialog = false) => {
        this.sendTo(peerId, {type: 'declined'});
        this.peerIsInRoom = false;
        if (openDeclineDialog) {
            try {
                this.lastDeclinedUser = manageStore.getStudent(peerId);
                this.dialogActions.openDialog(null, DeclineStudentDialogId);
            } catch (err) {
                console.log('failed to set last declined user', err);
            }
        } else {
            this.remoteVideoState = RemoteVideoState.Init;
            this.peerUser = null;
        }
    }

    @action
    stopVideoChat = () => {
        this.videoChatStarted = false;
    }

    @action
    prepareVideo = async () => {
        await this.startViewer();
    }

    updateDeviceList = async (skipSetInputDevice = false) => {
        if (this.viewer) {
            if (!skipSetInputDevice) {
                await this.viewer.setInputDevicesAsync({audioDeviceId: this.selectedAudioDeviceId, videoDeviceId: this.selectedVideoDeviceId});
            }

            const devicesList = await this.viewer.enumerateDevices();
            runInAction(() => {
                this.audioDevices = devicesList?.devices.filter(d => d.kind === 'audioinput').map((input) => {
                    return {
                        value: input.deviceId,
                        label: input.label,
                        selected: input.deviceId === this.selectedAudioDeviceId
                    };
                });
                this.videoDevices = devicesList?.devices.filter(d => d.kind === 'videoinput').map((input) => {
                    return {
                        value: input.deviceId,
                        label: input.label,
                        selected: input.deviceId === this.selectedVideoDeviceId
                    };
                });

            })
        }
    }

    @action
    setVideoDevice = async (videoDeviceId) => {
        this.videoMuted = false;
        this.selectedVideoDeviceId = videoDeviceId;
        await this.updateDeviceList();
    }

    @action
    setAudioDevice = async (audioDeviceId) => {
        this.audioMuted = false;
        this.selectedAudioDeviceId = audioDeviceId;
        await this.updateDeviceList();
    }

    @action toggleLocalMirroring = () => {
        this.localMirroringActive = !this.localMirroringActive;
        this.sendMirroringMessage();
    }


    @action
    setupDeviceChange = () => {
        navigator.mediaDevices.ondevicechange = async () => {
            await this.updateDeviceList();
        };
    }

    @action
    switchVideoDevice = async () => {
        await this.viewer.cycleCamera();
        await this.updateDeviceList();
        return true;
    }

    @action
    toggleMuteSpeaker = async () => {
        this.speakerMuted = !this.speakerMuted;
    }

    @action
    toggleMinimizeMode = async () => {
        this.minimizeMode = !this.minimizeMode;
    }

    @action
    toggleMuteAudio = () => {
        this.audioMuted = !this.audioMuted;
        if (this.viewer) {
            this.viewer.setLocalAudio(!this.audioMuted)
        }
    }

    @action
    toggleMuteVideo = () => {
        this.videoMuted = !this.videoMuted;
        if (this.viewer) {
            this.viewer.setLocalVideo(!this.videoMuted)
        }
    }

    @action
    setFullScreen = (val) => {
        this.fullScreen = val;
    }

    @computed get hasScreenShare() {
        return !!this.screenShareStreamId;
    }

    @action
    toggleScreenShare = () => {
        this.screenShared = !this.screenShared;
        if (this.screenShared) {
            this.viewer.startScreenShare({audio: true});
        } else {
            this.viewer.stopScreenShare();
        }
    }

}


const
    videoChatStore = new DailyVideoChatStore();
export default videoChatStore;
